import { ListActivityAPIQueryParams, listActivity } from '@goldcast/api/backend';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { AlignedRecordings } from '../types/clip-types';
import { SORT_ORDER_FOR_ENTITIES } from '../utils/alignedRecordingUtils';

export const fetchAlignedRecordings = async ({
  broadcast_id,
  usePureCloudRecording,
  queryParams = {},
  contentMetadataStoreInstance
}: {
  broadcast_id: string;
  usePureCloudRecording?: boolean;
  queryParams?: ListActivityAPIQueryParams;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
}) => {
  const entities = ['aligned_user_feed', 'aligned_screenshare_feed', 'aligned_video_feed'];

  const { results } = await listActivity({
    queryParams: {
      broadcast_id,
      // "as any" needs to be done because of no type support for arrays
      entity: entities as any,
      ...queryParams
    },
    queryParamsStringifyOptions: {
      arrayFormat: 'comma'
    }
  });

  if (!results) {
    contentMetadataStoreInstance.getState().updateAlignedRecording({
      contentId: broadcast_id,
      alignedRecordings: []
    });
    return;
  }

  const userIdToCloudRecordingMap: Record<string, string> = {};
  const userIdToLocalRecordingMap: Record<string, string> = {};

  results.forEach(recording => {
    if (recording.source === 'local') {
      userIdToLocalRecordingMap[recording.user.id + recording.entity] = recording.id;
    } else {
      userIdToCloudRecordingMap[recording.user.id + recording.entity] = recording.id;
    }
  });

  const normalizedResults = (results.filter(Boolean) as NonNullable<(typeof results)[number]>[]) ?? [];

  const sortedRecordings = [...normalizedResults].sort((a, b) => {
    // Keep screenshare aligned recordings ordered first for existing rendering logic.
    const aIndex = SORT_ORDER_FOR_ENTITIES[a.entity] ?? 0;
    const bIndex = SORT_ORDER_FOR_ENTITIES[b.entity] ?? 0;
    return aIndex - bIndex;
  });

  const recordingsWithCloudMapping = sortedRecordings.reduce<AlignedRecordings[]>((acc, recording) => {
    if (typeof usePureCloudRecording === 'boolean') {
      if (!usePureCloudRecording && recording.source === 'cloud') {
        return acc;
      }

      if (usePureCloudRecording && recording.source === 'local') {
        return acc;
      }
    } else if (recording.source === 'cloud' && userIdToLocalRecordingMap[recording.user.id + recording.entity]) {
      // if ff=off and local exists, then filter out cloud recording
      return acc;
    }

    acc.push({
      id: recording.id,
      entity: recording.entity,
      transcript_mapping: recording.transcript_mapping,
      stage_time_spans: recording.stage_time_spans,
      user: recording.user,
      mp4_url: (recording as any).mp4_url,
      preview_url: (recording as any).preview_url,
      preview_audio_url: (recording as any).preview_audio_url,
      thumbnail: recording.thumbnail,
      source: recording.source,
      video_metadata: recording.video_metadata,
      associatedCloudRecordingId:
        recording.source === 'local' ? userIdToCloudRecordingMap[recording.user.id + recording.entity] : undefined
    });

    return acc;
  }, []);

  contentMetadataStoreInstance.getState().updateAlignedRecording({
    contentId: broadcast_id,
    alignedRecordings: recordingsWithCloudMapping
  });
};
